import { useEffect, useState } from 'react';
import { api, ApiError, type Track } from '../../api.js';
import ConfirmDialog from '../../components/ConfirmDialog.js';
import ManifestBlock from '../../components/ManifestBlock.js';
import ModuleChip from '../../components/ModuleChip.js';
import PrereqBlock from '../../components/PrereqBlock.js';
import RepoBadge from '../../components/RepoBadge.js';
import ScorecardMatrix, { type Scorecard } from '../../components/ScorecardMatrix.js';
import ThemedTimeline from './ThemedTimeline.js';
import TrackIdBadge from './TrackIdBadge.js';
import { attentionStateFor, statusForTrackState } from './statusMap.js';
import { useTrackDetail } from './useTrackDetail.js';

interface TrackWithReview extends Track {
  review_verdict?: 'approve' | 'flag' | 'block' | null;
  review_summary?: string | null;
  review_scorecard?: Scorecard | null;
  ciso_verdict?: 'approve' | 'flag' | 'block' | null;
  ciso_summary?: string | null;
  ciso_scorecard?: Scorecard | null;
  architect_verdict?: 'approve' | 'flag' | 'block' | null;
  architect_summary?: string | null;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

interface CiIndeterminateInfo {
  detail: string;
  ci_lookup_status: number | null;
  ci_lookup_message: string | null;
}

async function mergeTrack(id: string, acknowledgeUnverifiedCi?: boolean): Promise<void> {
  const res = await fetch(`/api/tracks/${id}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(acknowledgeUnverifiedCi ? { acknowledge_unverified_ci: true } : {}),
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? body?.detail ?? `request failed: ${res.status}`,
      body?.blocked,
      body
    );
  }
}

export default function TrackDrawer({
  trackId,
  tracks,
  onClose,
  refreshList,
}: {
  trackId: string | null;
  tracks: Track[];
  onClose: () => void;
  refreshList: () => void;
}) {
  const { track, busy, error, act } = useTrackDetail(trackId, tracks, refreshList);
  const [mergeConfirmed, setMergeConfirmed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [ciConfirm, setCiConfirm] = useState<CiIndeterminateInfo | null>(null);

  useEffect(() => {
    setMergeConfirmed(false);
    setCancelling(false);
    setCancelReason('');
    setCiConfirm(null);
  }, [trackId]);

  useEffect(() => {
    if (!trackId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [trackId, onClose]);

  if (!trackId) return null;

  const reviewedTrack = track as TrackWithReview | null;
  const attention = track ? attentionStateFor(track) : null;
  const hasPrereqs = track ? (track.spec.prereqs ?? []).length > 0 : false;
  const actions: React.ReactNode[] = [];

  const cancelButton = (
    <button key="cancel" className="btn-outline-conductor drawer-btn-danger" disabled={busy}
      onClick={() => setCancelling(true)}>
      Cancel
    </button>
  );

  if (track) {
    if (track.state === 'SPEC_REVIEW') {
      actions.push(
        <button key="approve" className="btn-conductor" disabled={busy}
          onClick={() => act(() => api.approve(track.id))}>
          {hasPrereqs ? 'Approve — needs prereqs first' : 'Approve & build'}
        </button>,
        <button key="revise" className="btn-outline-conductor" disabled={busy}
          onClick={() => act(() => api.revise(track.id))}>
          Revise
        </button>,
        cancelButton
      );
    } else if (track.state === 'AWAITING_PREREQS') {
      actions.push(
        <button key="prereqs" className="btn-conductor" disabled={busy}
          onClick={() => act(() => api.prereqsDone(track.id))}>
          I&apos;ve done the prereqs
        </button>,
        cancelButton
      );
    } else if (track.state === 'PR_OPEN') {
      if (track.pr_url) {
        actions.push(
          <a key="pr" className="btn-outline-conductor" href={track.pr_url} target="_blank" rel="noreferrer">
            View PR #{track.pr_number ?? ''}
          </a>
        );
      }
      actions.push(
        <button key="merge" className="btn-conductor" disabled={busy}
          onClick={() => act(async () => {
            try {
              await mergeTrack(track.id);
              setMergeConfirmed(true);
            } catch (e) {
              if (e instanceof ApiError && e.code === 'ci_status_indeterminate') {
                const details = (e.details ?? {}) as { ci_lookup_status?: number | null; ci_lookup_message?: string | null };
                setCiConfirm({
                  detail: e.message,
                  ci_lookup_status: details.ci_lookup_status ?? null,
                  ci_lookup_message: details.ci_lookup_message ?? null,
                });
                return;
              }
              throw e;
            }
          })}>
          Approve & Merge
        </button>,
        cancelButton
      );
    } else if (track.state !== 'DONE' && track.state !== 'MERGED' && track.state !== 'CANCELLED') {
      actions.push(cancelButton);
    }
  }

  return (
    <>
      <div className="track-drawer-backdrop" onClick={onClose} />
      <div className="track-drawer" role="dialog" aria-modal="true">
        <button type="button" className="track-drawer-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {!track && <div className="empty">Loading…</div>}

        {track && (
          <>
            <div className="track-drawer-header">
              <div className="badge-row" style={{ flexWrap: 'wrap' }}>
                <span className="build-status-chip" data-status={statusForTrackState(track.state).status}>
                  {statusForTrackState(track.state).label}
                </span>
                {attention && (
                  <span className="review-flag-chip" data-verdict={attention.kind === 'halted' ? 'block' : 'flag'}>
                    {attention.kind === 'halted' ? '⛔ Halted' : '⏸ Needs input'}
                    {attention.since ? ` · ${relativeTime(attention.since)}` : ''}
                  </span>
                )}
                <RepoBadge targetRepo={track.spec.target_repo} />
                <ModuleChip module={track.module} />
                <span className="risk-chip" data-risk={track.spec.risk_level}>
                  {track.spec.risk_level} risk
                </span>
              </div>
              <h2 className="track-drawer-title">{track.title}</h2>
              <div className="track-drawer-one-line">{track.one_line}</div>
              <div className="track-drawer-time">{relativeTime(track.updated_at)}</div>
              <TrackIdBadge id={track.id} className="track-drawer-id" />
              {track.session_url ? (
                <a
                  className="track-drawer-session-link"
                  href={track.session_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open session
                </a>
              ) : (
                <span
                  className="track-drawer-session-link track-drawer-session-link-disabled"
                  title="No session URL recorded for this track yet"
                >
                  Open session
                </span>
              )}
            </div>

            <div className="track-drawer-body">
              {error && <div className="track-drawer-error">{error}</div>}
              {attention && (
                <div className="track-drawer-review">
                  <h3>{attention.kind === 'halted' ? 'Halted' : 'Needs input'}</h3>
                  {attention.reason && (
                    <div className="track-drawer-review-summary">{attention.reason}</div>
                  )}
                  {attention.since && (
                    <div className="track-drawer-time">
                      {attention.kind === 'halted' ? 'halted' : 'waiting since'} {relativeTime(attention.since)}
                    </div>
                  )}
                </div>
              )}
              {mergeConfirmed && (
                <div className="track-drawer-confirm">Merge requested — waiting for GitHub</div>
              )}
              {ciConfirm && (
                <ConfirmDialog
                  title="CI status could not be verified"
                  message={
                    <>
                      <p>{ciConfirm.detail}</p>
                      {ciConfirm.ci_lookup_status !== null && (
                        <p>
                          GitHub returned status {ciConfirm.ci_lookup_status}
                          {ciConfirm.ci_lookup_message ? `: ${ciConfirm.ci_lookup_message}` : ''}.
                        </p>
                      )}
                      <p>Proceeding merges this PR without automated CI verification.</p>
                    </>
                  }
                  confirmLabel="Merge without verified CI"
                  danger
                  busy={busy}
                  onCancel={() => setCiConfirm(null)}
                  onConfirm={() =>
                    act(async () => {
                      setCiConfirm(null);
                      await mergeTrack(track.id, true);
                      setMergeConfirmed(true);
                    })
                  }
                />
              )}
              {actions.length > 0 && <div className="actions track-drawer-actions">{actions}</div>}

              {cancelling && (
                <div className="track-drawer-cancel-confirm">
                  <input
                    type="text"
                    placeholder="Reason for cancelling"
                    value={cancelReason}
                    disabled={busy}
                    onChange={(e) => setCancelReason(e.target.value)}
                    style={{ width: '100%', marginBottom: 8 }}
                  />
                  <div className="actions">
                    <button className="btn-conductor drawer-btn-danger" disabled={busy}
                      onClick={() => act(async () => {
                        await api.cancel(track.id, cancelReason.trim() || undefined);
                        setCancelling(false);
                      })}>
                      Confirm cancel
                    </button>
                    <button className="btn-outline-conductor" disabled={busy}
                      onClick={() => setCancelling(false)}>
                      Back
                    </button>
                  </div>
                </div>
              )}

              {track.state === 'CANCELLED' && (
                <div className="track-drawer-review">
                  <h3>Cancelled</h3>
                  <div className="track-drawer-review-summary">
                    Cancelled: {track.cancel_reason ?? '(no reason recorded — pre-dates this feature)'}
                  </div>
                  {track.cancelled_at && (
                    <div className="track-drawer-time">{relativeTime(track.cancelled_at)}</div>
                  )}
                </div>
              )}

              {reviewedTrack?.architect_verdict && (
                <div className="track-drawer-review">
                  <h3>Architect</h3>
                  <span className="verdict-chip" data-verdict={reviewedTrack.architect_verdict}>
                    {reviewedTrack.architect_verdict}
                  </span>
                  {reviewedTrack.architect_summary && (
                    <div className="track-drawer-review-summary">{reviewedTrack.architect_summary}</div>
                  )}
                </div>
              )}

              {reviewedTrack?.review_verdict && (
                <div className="track-drawer-review">
                  <h3>CTO Review</h3>
                  <ScorecardMatrix reviewer="cto" scorecard={reviewedTrack.review_scorecard} />
                  <details className="drawer-details drawer-details-review">
                    <summary>Full review</summary>
                    <span className="verdict-chip" data-verdict={reviewedTrack.review_verdict}>
                      {reviewedTrack.review_verdict}
                    </span>
                    {reviewedTrack.review_summary && (
                      <div className="track-drawer-review-summary">{reviewedTrack.review_summary}</div>
                    )}
                  </details>
                </div>
              )}

              {reviewedTrack?.ciso_verdict && (
                <div className="track-drawer-review">
                  <h3>CISO Review</h3>
                  <ScorecardMatrix reviewer="ciso" scorecard={reviewedTrack.ciso_scorecard} />
                  <details className="drawer-details drawer-details-review">
                    <summary>Full review</summary>
                    <span className="verdict-chip" data-verdict={reviewedTrack.ciso_verdict}>
                      {reviewedTrack.ciso_verdict}
                    </span>
                    {reviewedTrack.ciso_summary && (
                      <div className="track-drawer-review-summary">{reviewedTrack.ciso_summary}</div>
                    )}
                  </details>
                </div>
              )}

              {track.blocked_by.length > 0 && (
                <div className="track-drawer-blocked">
                  <h3>Blocked by</h3>
                  <ul>
                    {track.blocked_by.map((id) => (
                      <li key={id} className="code">{id}</li>
                    ))}
                  </ul>
                </div>
              )}

              {hasPrereqs && (
                <details className="drawer-details">
                  <summary>Prereqs</summary>
                  <PrereqBlock prereqs={track.spec.prereqs ?? []} />
                </details>
              )}

              <details className="drawer-details">
                <summary>Manifest</summary>
                <ManifestBlock spec={track.spec} />
              </details>

              <details className="drawer-details">
                <summary>Rollback</summary>
                <div className="kv">
                  <strong>tag:</strong>
                  <span className="code">{track.spec.rollback.git_tag}</span>
                </div>
                <div className="kv">
                  <strong>side effects:</strong>
                  <span>{track.spec.rollback.side_effects}</span>
                </div>
              </details>

              <details className="drawer-details">
                <summary>Success criteria</summary>
                <ul>
                  {track.spec.success_criteria.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </details>

              <ThemedTimeline trackId={track.id} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
